import { FC, memo, useMemo } from 'react';
import styled from 'styled-components';
import { ProjectStatusValues } from '@base/types';

import { Props } from './types';

const PASTEL_GREEN = '#90EE90';
const PASTEL_RED = '#ffb6c1';

const BackgroundBar = styled.div<{ height?: number }>`
    width: 200px;
    min-width: 200px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    min-height: ${(props) => props.height ?? 0}px;
    text-align: center;
`;

const FilledBar = styled.div<{ perc?: number; height: number; $filledColor?: string }>`
    position: absolute;
    height: ${(props) => props.height ?? 0}px;
    width: ${(props) => props.perc ?? 0}%;
    top: 0;
    left: 0;
    background-color: ${(props) => props.$filledColor || PASTEL_GREEN} !important;
`;

export const ProgressBar: FC<Props> = memo((props) => {
    const { value = 0, max = 100, height = 20, bgColor = 'light', status } = props;

    const isCompleted = useMemo(() => value === max || status === ProjectStatusValues.Completed, [value, max, status]);
    const isWaiting = useMemo(() => value === 0 && status, [value, status]);
    const isFailed = useMemo(() => status === 'Failed', [status]);
    const isCancelled = useMemo(() => status === 'Cancelled By Administator', [status]);
    const showRedBar = useMemo(() => isFailed || isCancelled, [isFailed, isCancelled]);

    const barColor = useMemo(() => {
        if (showRedBar) return PASTEL_RED;
        return PASTEL_GREEN;
    }, [showRedBar]);

    const displayText = useMemo(() => {
        if (isCompleted) return 'Completed';
        if (isCancelled) return 'Cancelled by Admin';
        if (isWaiting) return status;
        return `${value}%`;
    }, [isCompleted, isCancelled, isWaiting, status, value]);

    return (
        <BackgroundBar className={`bg-${bgColor} rounded overflow-hidden px-1`} height={height}>
            <span style={{ zIndex: 1, color: showRedBar ? '#dc3545' : 'inherit' }}>{displayText}</span>

            <FilledBar $filledColor={barColor} perc={isCompleted || showRedBar ? 100 : (value / max) * 100} height={height} />
        </BackgroundBar>
    );
});
ProgressBar.displayName = 'ProgressBar';
